import { Link } from "react-router-dom"
import TABLE_COLUMNS from "../const/TOURNAMENTS_TABLE_COLUMNS"
import { Tag, Tooltip, Typography, Button } from "antd"
import { EditFilled, DownloadOutlined } from "@ant-design/icons"
import { observer } from "mobx-react"
import { RoutePaths } from "../../../navigation/RoutePaths.enum"
import { toJS } from "mobx"
import { SellerService } from "../../../../domain/entities/SellerService"
import DataTable from "../../../components/DataTable/DataTable"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import DataTableRowsSkeleton from "../../../styledComponents/LoadingSkeletons/DataTableRowsSkeleton"
import { useEffect, useState } from "react"
import { TournamentsViewModel } from "../TournamentsViewModel"

interface SellerTableProps {
	data: Partial<SellerService>[]
	loading?: boolean
	isFetching?: boolean
	onChangePage?: (page: number, pageSize: number, lastPageFetched: number) => void
}

const extractClusters = (data: Partial<SellerService>[]) => {
	const allClusters = data
		.flatMap(s => s.clusters)
		.filter(c => !!c?.clusterName)
		.reduce(
			(clusters, c) => ({ ...clusters, [c!.clusterId]: c!.clusterName }),
			{} as { [clusterId: number]: string }
		)

	return Object.entries(allClusters).map(([clusterId, clusterName]) => ({ clusterId, clusterName }))
}

const TournamentsTable = ({ data, loading, isFetching, onChangePage }: SellerTableProps) => {
	const viewModel = useContainerInjection<TournamentsViewModel>("TournamentsViewModel")
	const [clusters, setClusters] = useState<{ clusterId: string; clusterName: string }[]>([])

	useEffect(() => {
		setClusters(extractClusters(data))
	}, [data])

	return (
		<div className="table-responsive">
			<DataTable
				onChangePage={(page, pageSize, lastPageFetched) => {
					viewModel.setTableConfig({ pageSize })
					onChangePage && onChangePage(page, pageSize, lastPageFetched)
				}}
				renderCustomLoadingComponent={() => <DataTableRowsSkeleton size="lg" />}
				loading={loading}
				tableConfig={{
					sort: viewModel.tableConfig.sort,
					pageSize: viewModel.tableConfig.pageSize
				}}
				onChange={data => {
					data.sorter &&
						viewModel.setTableConfig({ sort: { order: data.sorter.order, field: data.sorter.field } })
				}}
				isFetching={isFetching}
				columns={TABLE_COLUMNS()}
				columnFilters={{
					user: Array.from(new Set(data.filter(s => !!s.name).map(s => s.name!))).map(name => ({
						id: name,
						text: name,
						value: name
					})),
					clusters: clusters.map(c => ({
						id: c!.clusterId,
						text: `${c!.clusterId} - ${c!.clusterName}`,
						value: `${c!.clusterId} - ${c!.clusterName}`
					}))
				}}
				dataSource={data.map((service, idx) => ({
					key: service.id,
					id: service.id,
					user: service.name,
					service: (
						<>
							<div className="avatar-info">
								<Typography.Text>{service.title}</Typography.Text>
							</div>
						</>
					),
					clusters: (() => {
						const clusters = service.clusters || []
						return (
							<div
								style={{
									flexWrap: "wrap",
									display: "flex"
								}}
							>
								{clusters?.length
									? clusters?.map((c, idx) => (
											<Tag
												style={{ marginBottom: 3 }}
												key={idx}
												color={
													c.clusterName.length > 18
														? "geekblue"
														: c.clusterName.length > 12
														? "cyan"
														: "blue"
												}
											>
												{`${c.clusterId} - ${c.clusterName}`}
											</Tag>
									  ))
									: null}
							</div>
						)
					})(),
					editButton: (
						<div className="ant-employed" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
							<Link to={RoutePaths.TOURNAMENT_MEMBERS} state={{ service: toJS(service) }}>
								<Tooltip title={"View Members"}>
									<EditFilled />
								</Tooltip>
							</Link>
							<Tooltip title={"Exportar"}>
								<Button
									type="text"
									size="small"
									icon={<DownloadOutlined />}
									disabled={viewModel.isExporting}
									onClick={e => {
										e.stopPropagation()
										viewModel.exportTournament(service.id!, service.title || "torneo")
									}}
								/>
							</Tooltip>
						</div>
					)
				}))}
			/>
		</div>
	)
}

export default observer(TournamentsTable)
